import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Chapter 003 · THE LINE
 * ────────────────────────────────────────────
 * Editorial giant headline + VERTICAL scroll-driven carousel.
 *
 * Interaction model:
 *   - Outer wrapper is tall (viewport + extra scroll room).
 *   - Inner section is `position: sticky; top: 0; height: 100vh` → pins to
 *     the top of the viewport once the user reaches this section.
 *   - As the user keeps scrolling the wheel, page scroll is converted into
 *     "progress" (0 → PIECES.length − 1), which drives which card is centered.
 *   - Once all cards have been advanced through, the section releases and the
 *     page continues to scroll normally.
 *
 * Additional interactions:
 *   - Left column has step buttons / prev / next → they scroll the WINDOW to
 *     the target position so the pinned section reflects the change.
 *   - ↑ / ↓ arrow keys also work when the section area is focused.
 */

type Piece = {
  id: string;
  name: string;
  tag: string;
  year: string;
  spec: string;
  image: string;
  xOffset: number; // small horizontal offset for editorial feel
};

const PIECES: Piece[] = [
  {
    id: 'reference-one',
    name: 'REFERENCE ONE',
    tag: 'FLAGSHIP · 2024',
    year: '01',
    spec: 'Beryllium · Titanium · Class-A',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20commercial%20product%20photograph%20of%20a%20real%20premium%20flagship%20over-ear%20headphone%20in%20brushed%20titanium%20and%20champagne%20anodized%20aluminum%20with%20soft%20black%20memory%20foam%20leather%20earcups%2C%20resting%20on%20a%20warm%20cream%20matte%20paper%20studio%20surface%2C%20soft%20diffused%20natural%20window%20light%20from%20the%20upper%20left%2C%20crisp%20specular%20highlights%20on%20polished%20metal%20edges%2C%20visible%20microtexture%20of%20real%20aluminum%20grain%20and%20fine%20leather%20stitching%2C%20tiny%20dust%20particles%20catching%20the%20light%2C%20shallow%20depth%20of%20field%2C%20accurate%20soft%20contact%20shadow%2C%20shot%20on%20Hasselblad%20medium%20format%20camera%20with%2080mm%20lens%20at%20f4%2C%20ISO%20100%2C%20high%20dynamic%20range%2C%20magazine%20quality%20editorial%20product%20photography%2C%20cinematic%20color%20grading%2C%20razor%20sharp%20focus%20with%20lifelike%20realism&width=1600&height=1000&seq=sa-realistic-reference-one-v3&orientation=landscape',
    xOffset: 0,
  },
  {
    id: 'studio-edition',
    name: 'STUDIO EDITION',
    tag: 'PRO · 2023',
    year: '02',
    spec: 'Aluminum · Alcantara · Balanced',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20commercial%20product%20photograph%20of%20a%20real%20professional%20studio%20over-ear%20headphone%20in%20matte%20charcoal%20anodized%20aluminum%20with%20deep%20black%20alcantara%20suede%20earpads%20and%20a%20braided%20fabric%20cable%2C%20resting%20on%20a%20warm%20cream%20matte%20paper%20studio%20surface%2C%20soft%20diffused%20natural%20window%20light%20from%20the%20upper%20left%2C%20visible%20real%20alcantara%20fiber%20texture%20and%20subtle%20aluminum%20bead%20blast%20finish%2C%20accurate%20soft%20contact%20shadow%2C%20fine%20air%20dust%20specks%20in%20the%20light%20beam%2C%20shallow%20depth%20of%20field%2C%20shot%20on%20Hasselblad%20medium%20format%20camera%20with%2080mm%20lens%20at%20f4%2C%20ISO%20100%2C%20high%20dynamic%20range%2C%20magazine%20quality%20editorial%20product%20photography%2C%20cinematic%20color%20grading%2C%20razor%20sharp%20focus%20with%20lifelike%20realism&width=1600&height=1000&seq=sa-realistic-studio-edition-v3&orientation=landscape',
    xOffset: 56,
  },
  {
    id: 'archive-zero',
    name: 'ARCHIVE ZERO',
    tag: 'RESERVE · 2022',
    year: '03',
    spec: 'Walnut · Copper · Single-ended',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20commercial%20product%20photograph%20of%20a%20real%20limited%20edition%20heritage%20over-ear%20headphone%20combining%20solid%20dark%20walnut%20wood%20earcups%20with%20visible%20natural%20wood%20grain%20and%20warm%20polished%20copper%20metal%20accents%2C%20soft%20aged%20brown%20leather%20headband%20with%20hand%20stitching%2C%20resting%20on%20a%20warm%20cream%20matte%20paper%20studio%20surface%2C%20soft%20diffused%20natural%20window%20light%20from%20the%20upper%20left%2C%20visible%20fine%20wood%20grain%20pores%20and%20subtle%20copper%20patina%20reflections%2C%20real%20leather%20texture%20details%2C%20accurate%20soft%20contact%20shadow%2C%20shallow%20depth%20of%20field%2C%20shot%20on%20Hasselblad%20medium%20format%20camera%20with%2080mm%20lens%20at%20f4%2C%20ISO%20100%2C%20high%20dynamic%20range%2C%20magazine%20quality%20editorial%20product%20photography%2C%20cinematic%20color%20grading%2C%20razor%20sharp%20focus%20with%20lifelike%20realism&width=1600&height=1000&seq=sa-realistic-archive-zero-v3&orientation=landscape',
    xOffset: 18,
  },
  {
    id: 'nomad-mono',
    name: 'NOMAD MONO',
    tag: 'PORTABLE · 2024',
    year: '04',
    spec: 'Magnesium · Vegan Leather · USB-C DAC',
    image:
      'https://readdy.ai/api/search-image?query=Ultra%20photorealistic%20hyperreal%20commercial%20product%20photograph%20of%20a%20real%20compact%20portable%20on-ear%20headphone%20in%20warm%20sand%20beige%20magnesium%20alloy%20body%20with%20cream%20soft%20vegan%20leather%20earpads%20and%20a%20small%20USB-C%20DAC%20module%2C%20resting%20on%20a%20warm%20cream%20matte%20paper%20studio%20surface%2C%20soft%20diffused%20natural%20window%20light%20from%20the%20upper%20left%2C%20visible%20fine%20matte%20magnesium%20grain%20and%20realistic%20leather%20pore%20texture%2C%20subtle%20specular%20micro%20highlights%20on%20the%20metal%20yoke%2C%20accurate%20soft%20contact%20shadow%2C%20fine%20dust%20particles%20catching%20the%20light%2C%20shallow%20depth%20of%20field%2C%20shot%20on%20Hasselblad%20medium%20format%20camera%20with%2080mm%20lens%20at%20f4%2C%20ISO%20100%2C%20high%20dynamic%20range%2C%20magazine%20quality%20editorial%20product%20photography%2C%20cinematic%20color%20grading%2C%20razor%20sharp%20focus%20with%20lifelike%20realism&width=1600&height=1000&seq=sa-realistic-nomad-mono-v3&orientation=landscape',
    xOffset: 40,
  },
];

const CARD_WIDTH = 880; // px
const CARD_HEIGHT = 540; // px
const CARD_STRIDE = CARD_HEIGHT + 80; // vertical distance between neighboring cards
const SCROLL_PER_CARD = 520; // px of window scroll needed to advance one card

export default function SoundArchitecture() {
  const outerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  // Continuous progress in [0, PIECES.length - 1], driven by window scroll.
  const [progress, setProgress] = useState(0);
  const activeIdx = Math.max(
    0,
    Math.min(PIECES.length - 1, Math.round(progress))
  );

  // ─── in-view fade ───
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setInView(true);
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // ─── scroll-driven progress ───
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const compute = () => {
      const rect = outer.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) {
        setProgress(0);
        return;
      }
      const raw = -rect.top / total; // 0 at start of pin, 1 at end
      const clamped = Math.max(0, Math.min(1, raw));
      setProgress(clamped * (PIECES.length - 1));
    };

    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, []);

  // Scroll window to a specific card index by translating idx → outer scroll offset
  const scrollToIndex = useCallback((idx: number) => {
    const outer = outerRef.current;
    if (!outer) return;
    const clamped = Math.max(0, Math.min(PIECES.length - 1, idx));
    const rect = outer.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    if (total <= 0) return;
    const targetProgress = clamped / (PIECES.length - 1);
    const outerTop = window.scrollY + rect.top;
    window.scrollTo({
      top: outerTop + targetProgress * total,
      behavior: 'smooth',
    });
  }, []);

  const handlePrev = () => scrollToIndex(activeIdx - 1);
  const handleNext = () => scrollToIndex(activeIdx + 1);

  // ─── keyboard nav on section ───
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handlePrev();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleNext();
    }
  };

  const fadeUp = useMemo(
    () => (delay: number, distance = 24) => ({
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : `translateY(${distance}px)`,
      transition: `opacity 1000ms ease ${delay}ms, transform 1000ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
    }),
    [inView]
  );

  const isAtStart = activeIdx <= 0;
  const isAtEnd = activeIdx >= PIECES.length - 1;

  return (
    <div
      ref={outerRef}
      className="relative w-full"
      style={{
        backgroundColor: '#141B2A',
        // Outer wraps: 100vh of pinned section + extra scroll room to advance cards
        height: `calc(100vh + ${(PIECES.length - 1) * SCROLL_PER_CARD}px)`,
      }}
    >
      <section
        ref={sectionRef}
        id="sound"
        className="relative w-full"
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          minHeight: '760px',
          backgroundColor: '#141B2A',
          fontFamily:
            'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
          overflow: 'hidden',
        }}
      >
        {/* top hairline */}
        <div
          className="absolute left-0 right-0 top-0"
          style={{
            height: '1px',
            background:
              'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.16) 20%, rgba(255,255,255,0.16) 80%, rgba(255,255,255,0) 100%)',
          }}
        />

        {/* faint grid backdrop */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            maskImage:
              'radial-gradient(ellipse at 50% 30%, black 40%, transparent 78%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at 50% 30%, black 40%, transparent 78%)',
          }}
        />

        <div
          className="relative mx-auto h-full flex flex-col"
          style={{
            maxWidth: '1360px',
            paddingLeft: '56px',
            paddingRight: '56px',
            paddingTop: '44px',
            paddingBottom: '32px',
          }}
        >
          {/* Chapter tag */}
          <div
            className="uppercase"
            style={{
              color: '#7AA8EE',
              fontSize: '11px',
              letterSpacing: '0.32em',
              fontWeight: 500,
              marginBottom: '20px',
              ...fadeUp(60, 10),
            }}
          >
            003 — THE LINE
          </div>

          {/* Giant editorial headline */}
          <div
            className="relative flex items-center w-full"
            style={{
              marginBottom: '24px',
              ...fadeUp(160, 20),
            }}
          >
            <span
              style={{
                color: '#FFFFFF',
                fontSize: 'clamp(44px, 5.6vw, 88px)',
                fontWeight: 700,
                lineHeight: 0.9,
                letterSpacing: '-0.045em',
                whiteSpace: 'nowrap',
              }}
            >
              REFERENCE
            </span>
            <div
              className="relative"
              style={{
                flex: '1 1 auto',
                margin: '0 28px',
                height: '8px',
                minWidth: '60px',
              }}
            >
              <div
                className="absolute inset-y-0 left-0"
                style={{
                  width: inView ? '100%' : '0%',
                  height: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#7AA8EE',
                  transition: 'width 1400ms cubic-bezier(0.22,1,0.36,1) 420ms',
                  boxShadow: '0 0 20px rgba(122,168,238,0.35)',
                }}
              />
            </div>
            <span
              style={{
                color: '#FFFFFF',
                fontSize: 'clamp(44px, 5.6vw, 88px)',
                fontWeight: 700,
                lineHeight: 0.9,
                letterSpacing: '-0.045em',
                whiteSpace: 'nowrap',
              }}
            >
              LINE
            </span>
          </div>

          {/* ─── Two-column: left controls + right scroll-driven rail ─── */}
          <div
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="relative flex items-start outline-none flex-1 min-h-0"
            style={{ gap: '64px', ...fadeUp(360, 12) }}
          >
            {/* LEFT · controls column */}
            <div
              className="flex-none flex flex-col justify-between h-full"
              style={{ width: '220px', paddingTop: '8px', paddingBottom: '8px' }}
            >
              <div>
                {/* Big active index */}
                <div
                  className="flex items-baseline gap-4"
                  style={{ marginBottom: '28px' }}
                >
                  <span
                    style={{
                      color: '#FFFFFF',
                      fontSize: '54px',
                      fontWeight: 700,
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {String(activeIdx + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="uppercase"
                    style={{
                      color: 'rgba(255,255,255,0.42)',
                      fontSize: '11px',
                      letterSpacing: '0.32em',
                      fontWeight: 500,
                      paddingBottom: '6px',
                    }}
                  >
                    / {String(PIECES.length).padStart(2, '0')}
                  </span>
                </div>

                {/* Active name */}
                <div style={{ marginBottom: '32px', minHeight: '48px' }}>
                  <div
                    style={{
                      color: '#FFFFFF',
                      fontSize: '22px',
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                    }}
                  >
                    {PIECES[activeIdx].name}
                  </div>
                </div>

                {/* Vertical progress rail */}
                <div
                  className="flex flex-col items-start"
                  style={{ gap: '14px' }}
                >
                  {PIECES.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => scrollToIndex(i)}
                      aria-label={`Go to ${p.name}`}
                      className="cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color:
                          i === activeIdx
                            ? '#FFFFFF'
                            : 'rgba(255,255,255,0.38)',
                        transition: 'color 320ms ease',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          height: i === activeIdx ? '28px' : '10px',
                          width: '3px',
                          borderRadius: '2px',
                          background:
                            i === activeIdx
                              ? '#7AA8EE'
                              : 'rgba(255,255,255,0.22)',
                          transition:
                            'height 380ms cubic-bezier(0.22,1,0.36,1), background 380ms ease',
                        }}
                      />
                      <span
                        className="uppercase"
                        style={{
                          fontSize: '10.5px',
                          letterSpacing: '0.28em',
                          fontWeight: 500,
                        }}
                      >
                        {p.year}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Up / Down buttons */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={isAtStart}
                  aria-label="Previous piece"
                  className="cursor-pointer whitespace-nowrap flex items-center justify-center"
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.20)',
                    background: 'transparent',
                    color: '#FFFFFF',
                    opacity: isAtStart ? 0.28 : 1,
                    transition:
                      'opacity 320ms ease, background 320ms ease, border-color 320ms ease',
                    cursor: isAtStart ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (!isAtStart) {
                      e.currentTarget.style.background =
                        'rgba(122,168,238,0.08)';
                      e.currentTarget.style.borderColor =
                        'rgba(122,168,238,0.55)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor =
                      'rgba(255,255,255,0.20)';
                  }}
                >
                  <i
                    className="ri-arrow-up-line"
                    style={{ fontSize: '18px' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isAtEnd}
                  aria-label="Next piece"
                  className="cursor-pointer whitespace-nowrap flex items-center justify-center"
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.20)',
                    background: 'transparent',
                    color: '#FFFFFF',
                    opacity: isAtEnd ? 0.28 : 1,
                    transition:
                      'opacity 320ms ease, background 320ms ease, border-color 320ms ease',
                    cursor: isAtEnd ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (!isAtEnd) {
                      e.currentTarget.style.background =
                        'rgba(122,168,238,0.08)';
                      e.currentTarget.style.borderColor =
                        'rgba(122,168,238,0.55)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor =
                      'rgba(255,255,255,0.20)';
                  }}
                >
                  <i
                    className="ri-arrow-down-line"
                    style={{ fontSize: '18px' }}
                  />
                </button>
              </div>
            </div>

            {/* RIGHT · scroll-driven card stack */}
            <div
              className="relative flex-1 min-w-0 h-full"
              style={{ ...fadeUp(560, 20) }}
            >
              <div
                className="relative w-full h-full"
                style={{ overflow: 'hidden' }}
              >
                {/* Cards centered vertically inside the column */}
                <div
                  className="absolute left-0 right-0"
                  style={{
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: `${CARD_HEIGHT}px`,
                  }}
                >
                  {PIECES.map((piece, idx) => {
                    const delta = idx - progress; // negative = above, positive = below
                    const distance = Math.abs(delta);
                    const isActive = hovered === piece.id;
                    const isDim = hovered !== null && hovered !== piece.id;
                    const isCurrent = idx === activeIdx;

                    // slide vertically based on scroll progress
                    const translateY = delta * CARD_STRIDE;
                    // fade neighbors
                    const opacityByDistance = Math.max(
                      0,
                      1 - distance * 0.6
                    );
                    // slight scale down for far cards
                    const scale = Math.max(0.94, 1 - distance * 0.04);

                    return (
                      <article
                        key={piece.id}
                        onMouseEnter={() => setHovered(piece.id)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => scrollToIndex(idx)}
                        className="absolute cursor-pointer"
                        style={{
                          top: 0,
                          left: `${piece.xOffset}px`,
                          width: `${CARD_WIDTH}px`,
                          maxWidth: `calc(100% - ${piece.xOffset}px)`,
                          height: `${CARD_HEIGHT}px`,
                          transform: `translateY(${translateY}px) scale(${
                            isActive ? scale * 1.01 : scale
                          })`,
                          opacity:
                            (isDim ? 0.55 : 1) * opacityByDistance,
                          filter:
                            !isCurrent && !isActive
                              ? 'saturate(0.9)'
                              : 'none',
                          transition:
                            'opacity 260ms ease, filter 480ms ease',
                          pointerEvents: distance > 0.6 ? 'none' : 'auto',
                          willChange: 'transform, opacity',
                        }}
                      >
                        <div
                          className="relative w-full h-full overflow-hidden"
                          style={{
                            borderRadius: '2px',
                            border: isCurrent
                              ? '1px solid rgba(122,168,238,0.35)'
                              : '1px solid rgba(255,255,255,0.10)',
                            transition: 'border-color 420ms ease',
                          }}
                        >
                          <img
                            src={piece.image}
                            alt={piece.name}
                            draggable={false}
                            className="w-full h-full object-cover object-top"
                            style={{
                              display: 'block',
                              transform: isActive
                                ? 'scale(1.04)'
                                : 'scale(1)',
                              transition:
                                'transform 1400ms cubic-bezier(0.22,1,0.36,1)',
                              pointerEvents: 'none',
                            }}
                          />

                          <div
                            className="pointer-events-none absolute inset-0"
                            style={{
                              background:
                                'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0) 55%, rgba(0,0,0,0) 66%, rgba(0,0,0,0.58) 100%)',
                            }}
                          />

                          <div
                            className="absolute uppercase"
                            style={{
                              top: '20px',
                              left: '22px',
                              color: '#7AA8EE',
                              fontSize: '10.5px',
                              letterSpacing: '0.32em',
                              fontWeight: 500,
                            }}
                          >
                            {piece.tag}
                          </div>

                          <div
                            className="absolute flex items-end justify-between gap-6"
                            style={{
                              bottom: '24px',
                              left: '28px',
                              right: '28px',
                            }}
                          >
                            <h3
                              style={{
                                color: '#FFFFFF',
                                fontSize: '48px',
                                fontWeight: 700,
                                lineHeight: 0.95,
                                letterSpacing: '-0.025em',
                                margin: 0,
                                textShadow: '0 1px 24px rgba(0,0,0,0.35)',
                              }}
                            >
                              {piece.name}
                            </h3>
                            <span
                              className="uppercase inline-flex items-center gap-2 whitespace-nowrap"
                              style={{
                                color: '#FFFFFF',
                                fontSize: '10.5px',
                                letterSpacing: '0.32em',
                                fontWeight: 500,
                                transform: isActive
                                  ? 'translateX(-4px)'
                                  : 'translateX(0)',
                                transition:
                                  'transform 380ms cubic-bezier(0.22,1,0.36,1)',
                              }}
                            >
                              VIEW
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '18px',
                                  height: '1px',
                                  background: '#7AA8EE',
                                  transform: isActive
                                    ? 'scaleX(1.6)'
                                    : 'scaleX(1)',
                                  transformOrigin: 'left center',
                                  transition:
                                    'transform 380ms cubic-bezier(0.22,1,0.36,1)',
                                }}
                              />
                            </span>
                          </div>
                        </div>

                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer meta line */}
          <div
            className="flex items-baseline justify-between"
            style={{
              marginTop: '24px',
              paddingTop: '18px',
              borderTop: '1px solid rgba(255,255,255,0.10)',
              ...fadeUp(720, 14),
            }}
          >
            <span
              className="uppercase"
              style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: '10.5px',
                letterSpacing: '0.32em',
                fontWeight: 500,
              }}
            >
              SCROLL ↑ ↓
            </span>
            <span
              className="uppercase inline-flex items-center gap-3"
              style={{
                color: '#FFFFFF',
                fontSize: '11px',
                letterSpacing: '0.28em',
                fontWeight: 500,
              }}
            >
              FULL COLLECTION
              <span
                style={{
                  display: 'inline-block',
                  width: '48px',
                  height: '1px',
                  background: '#7AA8EE',
                }}
              />
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}